import React, {useEffect} from "react";
import {makeGetRequest} from "../../../Services/ServicesUtils";
import {API_AUDITS} from "../../../Models/Audit/AuditEndpoints";
import PropTypes from "prop-types";
import {capitalize} from "lodash";
import {AuditMediaThump} from "./AuditMediaThump";

export const AuditMediaViewerBody = ({
    auditID,
    defaultAudit = null,
    organizeView = 0,
}) => {
    const [audit, setAudit] = React.useState(defaultAudit);
    const [auditPins, setAuditPins] = React.useState([]);
    const [auditLines, setAuditLines] = React.useState([]);
    const [auditAreas, setAuditAreas] = React.useState([]);

    useEffect(() => {
        if (audit) {
            setAuditPins(audit.audit_issues);
            setAuditLines(audit.audit_lines);
            setAuditAreas(audit.audit_areas);
            return;
        }

        makeGetRequest(`${API_AUDITS.publicAudits}${auditID}/`).then(
            (res) => {
                setAudit(res);
                setAuditPins(res.audit_issues);
                setAuditLines(res.audit_lines);
                setAuditAreas(res.audit_areas);
            },
        );
    }, [audit, auditID]);

    if (!audit) {
        return <div>Loading...</div>;
    }

    function renderMediasForItem(images, videos, item) {
        const mediaFiles = [...images, ...videos];
        return (
            <div className="d-flex flex-wrap" style={{
                background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                padding: "20px",
                borderRadius: "12px",
                boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1), 0 3px 6px rgba(0, 0, 0, 0.08)",
            }}>
                {images.map((image, index) => {
                    return image.image_value &&
                        <AuditMediaThump
                            key={`image_${item.id}_${index}`}
                            index={index}
                            mediaObj={image}
                            isImage={true}
                            mediaFiles={mediaFiles}
                            item={item}
                        />;
                })
                }
                {videos.map((video, index) =>
                    video.video_value &&
                        <AuditMediaThump
                            key={`video_${item.id}_${index}`}
                            index={index}
                            mediaObj={video}
                            isImage={false}
                            mediaFiles={mediaFiles}
                            item={item}
                        />)
                }
            </div>
        );
    }

    function renderAllMediasAllItems(auditItems, itemKey) {
        const imageKey = itemKey + "_images";
        const videoKey = itemKey + "_videos";
        let title = capitalize(itemKey);
        if (title === "Issue") {
            title = "Pin";
        }

        return auditItems && auditItems.map((item, index) => {
            return (
                <div key={index} className="mb-3">
                    {(item[imageKey].length > 0 || item[videoKey].length > 0) &&
                        <>
                            <div className={"my-2"}>
                                {title} {index + 1} - {item.audit_type_item.name}- {item.severity_object.name}
                            </div>
                            {renderMediasForItem(item[imageKey], item[videoKey], item)}
                        </>
                    }
                </div>
            );
        });
    }

    function getMediaFiles(auditItems, itemKey) {
        const imageKey = itemKey + "_images";
        const videoKey = itemKey + "_videos";

        return auditItems.flatMap((item) => {
            const images = item[imageKey] || [];
            const videos = item[videoKey] || [];
            return [...images, ...videos];
        });
    }
    function renderAllMediasForItemType(auditItems, itemType) {
        const videoKey = itemType + "_videos";
        const imageKey = itemType + "_images";

        const mediaFiles = getMediaFiles(auditItems, itemType);

        return <>
            {auditItems && auditItems.map((item) => {
                return (
                    <>
                        {item[imageKey].length > 0 &&
                            <>
                                {item[imageKey].map((image, index) => {
                                    return (
                                        <>
                                            {image.image_value &&
                                                <AuditMediaThump
                                                    key={`image_key${image.id}_${index}`}
                                                    index={index}
                                                    mediaObj={image}
                                                    item={item}
                                                    mediaFiles={mediaFiles}
                                                />}
                                        </>
                                    );
                                })}
                            </>
                        }
                        {item[videoKey].length > 0 &&
                            <>
                                {item[videoKey].map((video, index) => {
                                    return (
                                        <>
                                            {video.video_value &&
                                                <AuditMediaThump
                                                    key={`video_key${video.id}_${index}`}
                                                    index={index}
                                                    mediaObj={video}
                                                    item={item}
                                                    isImage={false}
                                                    mediaFiles={mediaFiles}
                                                />
                                            }
                                        </>
                                    );
                                })}
                            </>
                        }
                    </>
                );
            })}
        </>;
    }

    function renderAllMediasAllItemTypes() {
        const canRenderPins = auditPins && auditPins.length > 0 && getMediaFiles(auditPins, "issue").length > 0;
        const canRenderLines = auditLines && auditLines.length > 0 && getMediaFiles(auditLines, "line").length > 0;
        const canRenderAreas = auditAreas && auditAreas.length > 0 && getMediaFiles(auditAreas, "area").length > 0;

        if (!canRenderPins && !canRenderLines && !canRenderAreas) {
            return <div>Inspection has items, but no media available</div>;
        }

        return (
            <> {canRenderPins &&
                <div
                    className="d-flex flex-nowrap my-3"
                    style={{
                        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                        padding: "20px",
                        borderRadius: "12px",
                        boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1), 0 3px 6px rgba(0, 0, 0, 0.08)",
                        overflowX: "auto",
                    }}
                >
                    Pins
                    {renderAllMediasForItemType(auditPins, "issue")}
                </div>
            }
            {canRenderLines &&
                <div
                    className="d-flex flex-nowrap my-3"
                    style={{
                        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                        padding: "20px",
                        borderRadius: "12px",
                        boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1), 0 3px 6px rgba(0, 0, 0, 0.08)",
                        overflowX: "auto",
                    }}
                >
                    Lines
                    {renderAllMediasForItemType(auditLines, "line")}
                </div>}
            {canRenderAreas &&
                <div
                    className="d-flex flex-nowrap my-3"
                    style={{
                        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                        padding: "20px",
                        borderRadius: "12px",
                        boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1), 0 3px 6px rgba(0, 0, 0, 0.08)",
                        overflowX: "auto",
                    }}
                >
                    Areas
                    {renderAllMediasForItemType(auditAreas, "area")}
                </div>
            }
            </>
        );
    }

    return <>
        <div className="container">
            <div className="mt-4">
                {organizeView === 0 &&
                     renderAllMediasAllItemTypes()
                }
                {organizeView === 1 &&
                    <>
                        {renderAllMediasAllItems(auditPins, "issue")}
                        {renderAllMediasAllItems(auditLines, "line")}
                        {renderAllMediasAllItems(auditAreas, "area")}
                    </>
                }
            </div>
        </div>
    </>;
};

AuditMediaViewerBody.propTypes = {
    auditID: PropTypes.number.isRequired,
    defaultAudit: PropTypes.object,
    organizeView: PropTypes.number,
};
